import { useMemo } from 'react';

import {
  alignToolForGeneration,
  GENERATION_TOOL,
  type GenerationTool,
  isPresetFreestyleToolId,
} from 'src/data/generationTool';
// @ts-expect-error - JS module without type declarations
import { useFreestyleTools } from 'src/services/useFreestyleTools';

const MAX_CHARS_FOR_PROMPT_ROW = 74;

interface FreestyleTool {
  id: string;
  name: string;
  special: boolean;
  generationTool?: GenerationTool;
}

interface UseFreestyleToolChipsParams {
  generationTool: GenerationTool;
  isEnglish: boolean;
}

interface UseFreestyleToolChipsResult {
  visibleFreestyleTools: FreestyleTool[];
  hiddenFreestyleTools: FreestyleTool[];
}

export const useFreestyleToolChips = ({
  generationTool,
  isEnglish,
}: UseFreestyleToolChipsParams): UseFreestyleToolChipsResult => {
  const freestyleTools = useFreestyleTools() as FreestyleTool[] | null;

  const filteredFreestyleTools = useMemo(() => {
    if (!freestyleTools) {
      return [];
    }

    const alignedTool = alignToolForGeneration(generationTool);
    return (
      freestyleTools
        .filter((freestyleTool) => {
          if (!freestyleTool.generationTool) {
            return generationTool === GENERATION_TOOL.freestyle;
          }

          return freestyleTool.generationTool === alignedTool;
        })
        // special prompts should be displayed first
        .sort((a, b) => Number(b.special) - Number(a.special))
    );
  }, [freestyleTools, generationTool]);

  const numberOfToolsToShow = useMemo(() => {
    const toolsLength = filteredFreestyleTools.map((tool) => tool.name.length + 8);

    let result = 0;
    let currentRow = 1;
    let totalLength = 0;

    toolsLength.forEach((toolLength) => {
      if (currentRow <= 2) {
        totalLength += toolLength;
        if (totalLength > MAX_CHARS_FOR_PROMPT_ROW) {
          currentRow++;
          totalLength = 20 + toolLength; // more button needs to fit here
        }
        if (currentRow <= 2) {
          result++;
        }
      }
    });

    return result;
  }, [filteredFreestyleTools]);

  return useMemo(() => {
    // Filtering out preset tools if the language is non-English
    const presetTools = !isEnglish
      ? filteredFreestyleTools.filter((tool) => !isPresetFreestyleToolId(tool.id))
      : filteredFreestyleTools;

    const visibleCount =
      filteredFreestyleTools.length < numberOfToolsToShow
        ? filteredFreestyleTools.length
        : numberOfToolsToShow;

    return {
      visibleFreestyleTools: presetTools.slice(0, visibleCount),
      hiddenFreestyleTools: presetTools.slice(visibleCount, presetTools.length),
    };
  }, [filteredFreestyleTools, isEnglish, numberOfToolsToShow]);
};
